import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { STORAGE_KEYS } from "../../utils/constants";
import axios from "axios";
import {
  ShoppingCartIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ClockIcon,
  ExclamationCircleIcon,
  StarIcon,
  ShieldCheckIcon,
  SparklesIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";

// ─── Image Cache ─────────────────────────────────────────────────────────────
class ImageCache {
  constructor() {
    this.cache = new Map();
    this.failed = new Set();
  }
  markFailed(src) { this.failed.add(src); }
  preload(src) {
    if (!src || this.cache.has(src) || this.failed.has(src)) return Promise.resolve(src);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => { this.cache.set(src, src); resolve(src); };
      img.onerror = () => { this.failed.add(src); reject(new Error("Failed")); };
      img.src = src;
    });
  }
}
const imageCache = new ImageCache();

const OptimizedImage = ({ src, alt, className, fallbackSrc = "/images/default-bike.png" }) => {
  const [imageSrc, setImageSrc] = useState(src || fallbackSrc);
  useEffect(() => { setImageSrc(src || fallbackSrc); }, [src, fallbackSrc]);
  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      loading="lazy"
      onError={(e) => {
        e.currentTarget.onerror = null;
        imageCache.markFailed(imageSrc);
        setImageSrc(fallbackSrc);
      }}
    />
  );
};

// ─── Grid overlay (matches About page dark sections) ────────────────────────
const GridOverlay = () => (
  <div
    className="absolute inset-0 opacity-[0.04] pointer-events-none"
    style={{
      backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
      backgroundSize: "40px 40px",
    }}
  />
);

// ─── Main Page ───────────────────────────────────────────────────────────────
const ServicePackagesPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const BASE_URL = (import.meta.env.VITE_BASE_URL || "http://localhost:8080").replace(/\/+$/, "");

  const [bikeData, setBikeData] = useState({
    brand: "Unknown", model: "Unknown",
    image: "/images/default-bike.png", brandLogo: "",
    brandId: null, modelId: null,
  });

  const [allServices, setAllServices] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState("General Services");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [showCartSidebar, setShowCartSidebar] = useState(false);

  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);

  const serviceCategories = useMemo(() => [
    { id: 1, name: "General Services", icon: "🔧", description: "Regular maintenance" },
    { id: 2, name: "Bike Repairing",   icon: "⚙️", description: "Expert repairs" },
  ], []);

  const normalizeUploadPath = useCallback((p) => {
    if (!p) return "";
    return p.replaceAll("\\\\", "/").replaceAll("\\", "/");
  }, []);

  const buildImageUrl = useCallback((p) => {
    if (!p) return "/images/default-bike.png";
    if (p.startsWith("http")) return p;
    const cleaned = normalizeUploadPath(p).replace(/^\/+/, "");
    return `${BASE_URL}/${cleaned}`;
  }, [BASE_URL, normalizeUploadPath]);

  const formatServicesData = useCallback((services) => {
    if (!Array.isArray(services)) return [];
    return services.map((s, idx) => {
      const img = s.service_image ? buildImageUrl(s.service_image) : "/images/default-bike.png";
      if (img && img !== "/images/default-bike.png") imageCache.preload(img).catch(() => {});
      return {
        id: s.service_id ?? `service-${idx + 1}`,
        name: s.name || `Service ${idx + 1}`,
        originalPrice: Number(s.price) || 0,
        discountedPrice: Number(s.price) || 0,
        image: img,
        category: s.type || "General Services",
        duration: s.duration || "2-3 hours",
        rating: 4.8,
        reviews: 156,
      };
    });
  }, [buildImageUrl]);

  const fetchAllServices = useCallback(async () => {
    if (!bikeData.brandId || !bikeData.modelId) {
      setError("Brand or model information is missing.");
      return;
    }
    if (isFetchingRef.current && abortControllerRef.current) abortControllerRef.current.abort();
    if (abortControllerRef.current) abortControllerRef.current.abort();

    const controller = new AbortController();
    abortControllerRef.current = controller;
    isFetchingRef.current = true;

    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${BASE_URL}/admin/service/search`, {
        params: { brand_id: bikeData.brandId, model_id: bikeData.modelId },
        headers: { Accept: "application/json" },
        timeout: 15000,
        signal: controller.signal,
      });
      const raw = Array.isArray(res.data) ? res.data : res.data?.services || [];
      const formatted = formatServicesData(raw);
      setAllServices(formatted);
      setServicesLoaded(true);
      const types = Array.from(new Set(formatted.map((x) => x.category).filter(Boolean)));
      if (types.length && !types.includes(activeCategory)) setActiveCategory(types[0]);
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err?.response?.data?.message || "Failed to load services. Please try again.");
      setAllServices([]);
      setServicesLoaded(false);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [BASE_URL, bikeData.brandId, bikeData.modelId, formatServicesData, activeCategory]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!state || !state.bikeData) return;
    const bikeInfo = state.bikeData;
    if (!bikeInfo.brandId || !bikeInfo.modelId) {
      setError("Invalid bike selection. Missing brand or model information.");
      return;
    }
    setBikeData({
      brand: bikeInfo.brand || "Unknown",
      model: bikeInfo.model || "Unknown",
      image: bikeInfo.image || "/images/default-bike.png",
      brandLogo: bikeInfo.brandLogo || "",
      brandId: bikeInfo.brandId,
      modelId: bikeInfo.modelId,
    });
    if (Array.isArray(bikeInfo.services) && bikeInfo.services.length > 0) {
      const formatted = formatServicesData(bikeInfo.services);
      setAllServices(formatted);
      setServicesLoaded(true);
      setError(null);
      const types = Array.from(new Set(formatted.map((x) => x.category).filter(Boolean)));
      if (types.length) setActiveCategory(types.includes(activeCategory) ? activeCategory : types[0]);
    } else {
      setServicesLoaded(false);
    }
  }, [state, formatServicesData]);

  useEffect(() => {
    if (bikeData.brandId && bikeData.modelId && !servicesLoaded) fetchAllServices();
  }, [bikeData.brandId, bikeData.modelId, servicesLoaded, fetchAllServices]);

  // ✅ Restore pending checkout state after returning from login
  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return;
    const saved = sessionStorage.getItem("pending_checkout");
    if (!saved) return;
    try {
      const { cartItems: savedCart, bikeData: savedBike, savedAt } = JSON.parse(saved);
      if (Date.now() - savedAt > 30 * 60 * 1000) { sessionStorage.removeItem("pending_checkout"); return; }
      if (savedCart?.length > 0) setCartItems(savedCart);
      if (savedBike?.brandId) setBikeData(savedBike);
      sessionStorage.removeItem("pending_checkout");
      if (savedCart?.length > 0 && savedBike?.brandId) {
        const subtotalRestored = savedCart.reduce((sum, item) => sum + (Number(item.discountedPrice) || 0), 0);
        navigate("/checkout", { state: { cartItems: savedCart, bikeData: savedBike, store: "Hinjewadi", subtotal: subtotalRestored } });
      }
    } catch (e) { sessionStorage.removeItem("pending_checkout"); }
  }, []);

  const toggleCartItem = useCallback((item) => {
    setCartItems((prev) => {
      const exists = prev.some((x) => x.id === item.id);
      return exists ? prev.filter((x) => x.id !== item.id) : [...prev, item];
    });
  }, []);

  const isInCart = useCallback((id) => cartItems.some((x) => x.id === id), [cartItems]);

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + (Number(item.discountedPrice) || 0), 0),
    [cartItems]
  );

  const filteredServices = useMemo(
    () => allServices.filter((s) => s.category === activeCategory),
    [allServices, activeCategory]
  );

  const handleCheckout = useCallback(() => {
    if (cartItems.length === 0) { alert("Please select at least one service"); return; }
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      sessionStorage.setItem("pending_checkout", JSON.stringify({ cartItems, bikeData, subtotal, savedAt: Date.now() }));
      const returnUrl = encodeURIComponent("/service-packages");
      navigate(`/login?returnUrl=${returnUrl}`, { state: { message: "Please login to proceed with checkout", returnUrl: "/service-packages" } });
      return;
    }
    navigate("/checkout", { state: { cartItems, bikeData, store: "Hinjewadi", subtotal } });
  }, [cartItems, bikeData, navigate, subtotal]);

  // ─── No Bike Selected ────────────────────────────────────────────────────
  if (!state || !state.bikeData) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center p-4 relative overflow-hidden">
        <GridOverlay />
        <div className="relative z-10 bg-[#1a1a1a] border border-white/10 p-10 rounded-3xl text-center max-w-md shadow-2xl">
          <div className="w-20 h-20 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <ExclamationCircleIcon className="h-10 w-10 text-[#FF6B35]" />
          </div>
          <h2 className="text-2xl font-black text-white mb-3">No Bike Selected</h2>
          <p className="text-gray-400 mb-8 text-sm leading-relaxed">
            Please select a bike first to view available services.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-[#FF6B35] hover:bg-[#E85520] text-white rounded-xl font-bold transition-all shadow-lg shadow-[#FF6B35]/20 hover:-translate-y-0.5"
          >
            Select Your Bike
          </button>
        </div>
      </div>
    );
  }

  // ─── Main Render ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-8">

      {/* ── Hero Header (dark, matches About page hero) ── */}
      <div className="bg-[#111111] text-white relative overflow-hidden">
        <GridOverlay />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 bg-white/10 border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/20 transition-all"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-xs font-semibold mb-2">
                <span className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full animate-pulse" />
                Servicing for
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                {bikeData.brand}{" "}
                <span className="text-[#FF6B35]">{bikeData.model}</span>
              </h1>
            </div>

            {/* Mobile cart button */}
            <button
              onClick={() => setShowCartSidebar(true)}
              className="lg:hidden relative w-12 h-12 bg-white/10 border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/20 transition-all"
            >
              <ShoppingCartIcon className="h-6 w-6" />
              {cartItems.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-[#FF6B35] text-white text-xs font-black w-5 h-5 rounded-full flex items-center justify-center">
                  {cartItems.length}
                </span>
              )}
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center gap-5 text-sm text-gray-400">
            <div className="flex items-center gap-1.5">
              <ShieldCheckIcon className="w-4 h-4 text-[#FF6B35]" />
              <span>100% Genuine Parts</span>
            </div>
            <div className="flex items-center gap-1.5">
              <StarSolidIcon className="w-4 h-4 text-yellow-400" />
              <span>4.8 Rating</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircleIcon className="w-4 h-4 text-[#FF6B35]" />
              <span>Certified Mechanics</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Main content ── */}
          <div className="flex-1">

            {/* Category selector */}
            <div className="mb-8">
              <h2 className="text-xl font-black text-gray-900 mb-4">Choose Service Type</h2>
              <div className="grid grid-cols-2 gap-3">
                {serviceCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => { setError(null); setActiveCategory(category.name); }}
                    className={`relative p-5 rounded-2xl border-2 transition-all text-left group ${
                      activeCategory === category.name
                        ? "border-[#FF6B35] bg-[#FF6B35]/5 shadow-lg shadow-[#FF6B35]/10"
                        : "border-gray-200 bg-white hover:border-[#FF6B35]/40 hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`text-3xl transition-transform ${activeCategory === category.name ? "scale-110" : "group-hover:scale-105"}`}>
                        {category.icon}
                      </div>
                      <div>
                        <h3 className={`font-bold text-sm ${activeCategory === category.name ? "text-[#FF6B35]" : "text-gray-900"}`}>
                          {category.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">{category.description}</p>
                      </div>
                    </div>
                    {activeCategory === category.name && (
                      <div className="absolute top-3 right-3">
                        <div className="w-5 h-5 bg-[#FF6B35] rounded-full flex items-center justify-center">
                          <CheckCircleIcon className="h-4 w-4 text-white" />
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-14 text-center">
                <div className="relative w-14 h-14 mx-auto mb-5">
                  <div className="absolute inset-0 border-4 border-gray-200 rounded-full" />
                  <div className="absolute inset-0 border-4 border-[#FF6B35] rounded-full border-t-transparent animate-spin" />
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-1">Loading Services</h3>
                <p className="text-gray-500 text-sm">Fetching best options for your bike…</p>
              </div>
            )}

            {/* Error */}
            {error && !loading && (
              <div className="bg-white rounded-2xl border-2 border-red-200 p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-gray-900 mb-1">Unable to Load Services</h3>
                    <p className="text-gray-500 text-sm mb-4">{error}</p>
                    <button
                      onClick={fetchAllServices}
                      className="px-5 py-2 bg-[#111111] text-white rounded-xl hover:bg-gray-800 font-bold text-sm transition-all"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Empty */}
            {!loading && !error && filteredServices.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-14 text-center">
                <div className="text-5xl mb-4">🏍️</div>
                <h3 className="text-lg font-black text-gray-900 mb-2">No Services Available</h3>
                <p className="text-gray-500 text-sm">
                  No {activeCategory.toLowerCase()} found for your {bikeData.brand} {bikeData.model}.
                </p>
              </div>
            )}

            {/* Services list */}
            {!loading && !error && filteredServices.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-black text-gray-900">Available Services</h3>
                  <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {filteredServices.length} services
                  </span>
                </div>
                {filteredServices.map((pkg) => (
                  <ServiceCard key={pkg.id} pkg={pkg} isInCart={isInCart} toggleCartItem={toggleCartItem} />
                ))}
              </div>
            )}
          </div>

          {/* Desktop cart */}
          <DesktopCart
            bikeData={bikeData}
            cartItems={cartItems}
            subtotal={subtotal}
            toggleCartItem={toggleCartItem}
            handleCheckout={handleCheckout}
          />
        </div>
      </div>

      {/* Mobile Cart Sidebar */}
      <MobileCartSidebar
        show={showCartSidebar}
        onClose={() => setShowCartSidebar(false)}
        bikeData={bikeData}
        cartItems={cartItems}
        subtotal={subtotal}
        toggleCartItem={toggleCartItem}
        handleCheckout={handleCheckout}
      />

      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#111111] border-t border-white/10 px-4 py-3 z-30">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">
              {cartItems.length} service{cartItems.length !== 1 ? "s" : ""} selected
            </p>
            <p className="text-2xl font-black text-white">₹{subtotal}</p>
          </div>
          <button
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
              cartItems.length === 0
                ? "bg-white/10 text-gray-500 cursor-not-allowed"
                : "bg-[#FF6B35] hover:bg-[#E85520] text-white shadow-lg shadow-[#FF6B35]/30 hover:-translate-y-0.5"
            }`}
            onClick={handleCheckout}
            disabled={cartItems.length === 0}
          >
            <span>{cartItems.length === 0 ? "Select Services" : "Checkout"}</span>
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Service Card ─────────────────────────────────────────────────────────────
const ServiceCard = ({ pkg, isInCart, toggleCartItem }) => {
  const savings = (Number(pkg.originalPrice) || 0) - (Number(pkg.discountedPrice) || 0);
  const inCart = isInCart(pkg.id);

  return (
    <div className={`bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden group hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#FF6B35]/5 ${
      inCart ? "border-[#FF6B35] shadow-lg shadow-[#FF6B35]/10" : "border-gray-100 shadow-md hover:border-[#FF6B35]/30"
    }`}>
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Image */}
          <div className="sm:w-36 sm:h-36 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
            <OptimizedImage
              src={pkg.image}
              alt={pkg.name}
              className="w-full h-44 sm:h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col justify-between gap-3">
            <div>
              <h3 className={`text-lg font-black mb-2 transition-colors ${inCart ? "text-[#FF6B35]" : "text-gray-900 group-hover:text-[#FF6B35]"}`}>
                {pkg.name}
              </h3>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-full">
                  <ClockIcon className="w-3.5 h-3.5 text-[#FF6B35]" />
                  <span className="text-xs font-medium">{pkg.duration}</span>
                </div>
                <div className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-full">
                  <StarSolidIcon className="w-3.5 h-3.5 text-yellow-400" />
                  <span className="text-xs font-semibold text-gray-900">{pkg.rating}</span>
                  <span className="text-xs text-gray-400">({pkg.reviews})</span>
                </div>
              </div>
            </div>

            {/* Price + CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-gray-100">
              <div className="flex items-baseline gap-2">
                {savings > 0 && (
                  <span className="text-gray-400 line-through text-base">₹{pkg.originalPrice}</span>
                )}
                <span className="text-2xl font-black text-gray-900">₹{pkg.discountedPrice}</span>
                {savings > 0 && (
                  <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    Save ₹{savings}
                  </span>
                )}
              </div>

              <button
                onClick={() => toggleCartItem(pkg)}
                className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${
                  inCart
                    ? "bg-[#FF6B35] text-white shadow-md shadow-[#FF6B35]/30 hover:bg-[#E85520]"
                    : "bg-[#111111] text-white hover:bg-gray-800 shadow-md hover:-translate-y-0.5"
                }`}
              >
                {inCart ? (
                  <><CheckCircleIcon className="w-4 h-4" /><span>Added</span></>
                ) : (
                  <><ShoppingCartIcon className="w-4 h-4" /><span>Add to Cart</span></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Desktop Cart ─────────────────────────────────────────────────────────────
const DesktopCart = ({ bikeData, cartItems, subtotal, toggleCartItem, handleCheckout }) => (
  <div className="hidden lg:block w-88" style={{ width: "22rem" }}>
    <div className="bg-white rounded-2xl border border-gray-100 shadow-xl sticky top-6 overflow-hidden">
      {/* Cart header — dark brand */}
      <div className="bg-[#111111] text-white p-5 relative overflow-hidden">
        <GridOverlay />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl flex items-center justify-center">
            <ShoppingCartIcon className="w-5 h-5 text-[#FF6B35]" />
          </div>
          <div>
            <h3 className="font-black text-white">Your Cart</h3>
            <p className="text-xs text-gray-400">{cartItems.length} item{cartItems.length !== 1 ? "s" : ""} selected</p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Empty */}
        {cartItems.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🛒</div>
            <p className="font-bold text-gray-900 mb-1">Cart is empty</p>
            <p className="text-gray-400 text-xs">Add services to get started</p>
          </div>
        ) : (
          <>
            <div className="space-y-2.5 mb-5 max-h-80 overflow-y-auto">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3.5 bg-[#FF6B35]/5 border border-[#FF6B35]/15 rounded-xl"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-900 text-xs mb-1 leading-snug truncate">{item.name}</h4>
                    <p className="text-base font-black text-[#FF6B35]">₹{item.discountedPrice}</p>
                  </div>
                  <button
                    onClick={() => toggleCartItem(item)}
                    className="w-7 h-7 bg-red-50 hover:bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              ))}
            </div>

            {/* Total + CTA */}
            <div className="border-t border-gray-100 pt-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-600">Total Amount</span>
                <span className="text-2xl font-black text-gray-900">₹{subtotal}</span>
              </div>
              <button
                className="w-full py-3.5 bg-[#FF6B35] hover:bg-[#E85520] text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#FF6B35]/25 hover:-translate-y-0.5"
                onClick={handleCheckout}
              >
                <span>Proceed to Checkout</span>
                <ArrowRightIcon className="w-4 h-4" />
              </button>
              <p className="text-center text-xs text-gray-400">🔒 Secure checkout • No hidden charges</p>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);

// ─── Mobile Cart Sidebar ──────────────────────────────────────────────────────
const MobileCartSidebar = ({ show, onClose, bikeData, cartItems, subtotal, toggleCartItem, handleCheckout }) => (
  <>
    {show && (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" onClick={onClose} />
    )}
    <div
      className={`fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white z-50 transform transition-transform duration-300 shadow-2xl ${
        show ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="bg-[#111111] text-white p-5 relative overflow-hidden">
          <GridOverlay />
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-black text-white">Your Cart</h3>
              <p className="text-xs text-gray-400">{cartItems.length} items selected</p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 bg-white/10 border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/20 transition-all"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🛒</div>
              <p className="font-bold text-gray-900">Your cart is empty</p>
              <p className="text-gray-400 text-sm mt-1">Add services to continue</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-4 bg-[#FF6B35]/5 border border-[#FF6B35]/15 rounded-xl">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1.5 text-sm">{item.name}</h4>
                    <p className="text-lg font-black text-[#FF6B35]">₹{item.discountedPrice}</p>
                  </div>
                  <button
                    onClick={() => toggleCartItem(item)}
                    className="w-8 h-8 bg-red-50 hover:bg-red-100 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-5 space-y-4 bg-white">
          <div className="flex justify-between items-center">
            <span className="text-sm font-semibold text-gray-600">Total Amount</span>
            <span className="text-2xl font-black text-gray-900">₹{subtotal}</span>
          </div>
          <button
            className={`w-full py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
              cartItems.length === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-[#FF6B35] hover:bg-[#E85520] text-white shadow-lg shadow-[#FF6B35]/25 hover:-translate-y-0.5"
            }`}
            onClick={() => { handleCheckout(); onClose(); }}
            disabled={cartItems.length === 0}
          >
            <span>CHECKOUT NOW</span>
            <ArrowRightIcon className="w-5 h-5" />
          </button>
          <p className="text-center text-xs text-gray-400">🔒 Secure checkout • No hidden charges</p>
        </div>
      </div>
    </div>
  </>
);

export default ServicePackagesPage;